import { useState, useEffect } from 'react'
import { ImportSession, FieldMapping } from '@retailsync/shared-types'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@retailsync/ui'

interface MappingStepProps {
  session: ImportSession
  onUpdate: (updates: Partial<ImportSession>) => void
}

const RETAILSYNC_FIELDS = [
  { key: 'name', label: 'Product Name', required: true, type: 'string' },
  { key: 'sku', label: 'SKU', required: true, type: 'string' },
  { key: 'category', label: 'Category', required: true, type: 'string' },
  { key: 'price', label: 'Price', required: false, type: 'currency' },
  { key: 'cost', label: 'Cost', required: false, type: 'currency' },
  { key: 'stock', label: 'Stock Quantity', required: false, type: 'number' },
  { key: 'description', label: 'Description', required: false, type: 'string' },
  { key: 'barcode', label: 'Barcode/UPC', required: false, type: 'string' },
  { key: 'batch_number', label: 'Batch Number', required: false, type: 'string' },
  { key: 'expiry_date', label: 'Expiry Date', required: false, type: 'date' }
]

export function MappingStep({ session, onUpdate }: MappingStepProps) {
  const [mappings, setMappings] = useState<FieldMapping[]>(session.mappings || [])
  const [detectedHeaders, setDetectedHeaders] = useState<string[]>([])

  // Define step order for navigation
  const STEP_ORDER = ['upload', 'format', 'mapping', 'preview', 'import'] as const
  type StepType = typeof STEP_ORDER[number]

  const getCurrentStepIndex = () => STEP_ORDER.indexOf(session.currentStep as StepType)
  const getNextStep = () => STEP_ORDER[getCurrentStepIndex() + 1] as StepType
  const getPreviousStep = () => STEP_ORDER[getCurrentStepIndex() - 1] as StepType

  const getMappedSourceField = (retailSyncField: string) => {
    const mapping = mappings.find(m => m.targetField === retailSyncField)
    return mapping?.sourceField || ''
  }

  const getUnmappedHeaders = () => {
    const mappedHeaders = mappings.map(m => m.sourceField)
    return detectedHeaders.filter(header => !mappedHeaders.includes(header))
  }

  useEffect(() => {
    // Detect headers from the parsed raw data (from engine)
    const headers: string[] = []
    if (session.rawData && session.rawData.length > 0) {
      const firstRow = session.rawData[0]
      headers.push(...Object.keys(firstRow))
    }

    if (headers.length > 0) {
      setDetectedHeaders(headers)
    } else {
      // Fallback mock headers if no parsed data yet
      setDetectedHeaders(['item name', 'sku', 'category', 'price', 'cost', 'quantity', 'description', 'upc'])
    }

    // Initialize mappings if not already done
    if (!mappings.length && session.selectedTemplate) {
      const initialMappings = session.selectedTemplate.defaultMappings.map(mapping => ({
        ...mapping,
        sourceField: mapping.sourceField
      }))
      setMappings(initialMappings)
      onUpdate({ mappings: initialMappings })
    }
  }, [session.selectedTemplate, session.rawData])

  const handleMappingChange = (retailSyncField: string, sourceField: string) => {
    const newMappings = mappings.map(mapping =>
      mapping.targetField === retailSyncField
        ? { ...mapping, sourceField }
        : mapping
    )

    // If this field wasn't mapped before, add it
    const existingMapping = mappings.find(m => m.targetField === retailSyncField)
    if (!existingMapping) {
      const fieldInfo = RETAILSYNC_FIELDS.find(f => f.key === retailSyncField)
      if (fieldInfo) {
        newMappings.push({
          sourceField,
          targetField: retailSyncField,
          transform: fieldInfo.type as 'string' | 'number' | 'date' | 'boolean' | 'currency',
          required: fieldInfo.required
        })
      }
    }

    setMappings(newMappings)
    onUpdate({ mappings: newMappings })
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Field Mapping</h3>
        <p className="text-gray-600 mb-6">
          Map your file columns to RetailsyncGo product fields. Required fields are marked with *.
        </p>
      </div>

      {/* Detected Headers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Detected Columns</CardTitle>
          <CardDescription>
            Columns found in your uploaded file
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {detectedHeaders.map((header, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
              >
                {header}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Field Mappings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Field Mappings</CardTitle>
          <CardDescription>
            Configure how each RetailsyncGo field maps to your data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {RETAILSYNC_FIELDS.map((field) => (
              <div key={field.key} className="flex items-center space-x-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </label>
                  <p className="text-xs text-gray-500">Type: {field.type}</p>
                </div>
                <div className="flex-1">
                  <select
                    value={getMappedSourceField(field.key)}
                    onChange={(e) => handleMappingChange(field.key, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Select Column --</option>
                    {detectedHeaders.map((header) => (
                      <option key={header} value={header}>
                        {header}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="text-sm text-gray-500">
                  {getMappedSourceField(field.key) && (
                    <span className="text-green-600">✓ Mapped</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Unmapped Columns */}
      {getUnmappedHeaders().length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="text-base text-yellow-800">Unmapped Columns</CardTitle>
            <CardDescription className="text-yellow-700">
              These columns from your file are not mapped to any RetailsyncGo fields
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {getUnmappedHeaders().map((header, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm"
                >
                  {header}
                </span>
              ))}
            </div>
            <p className="text-sm text-yellow-700 mt-2">
              Unmapped columns will be ignored during import.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Validation */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start">
          <span className="text-green-600 mr-2">✓</span>
          <div>
            <h3 className="text-sm font-medium text-green-800">Mapping Complete</h3>
            <p className="mt-1 text-sm text-green-700">
              {mappings.filter(m => m.sourceField).length} of {RETAILSYNC_FIELDS.filter(f => f.required).length} required fields mapped
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between">
        <button
          onClick={() => onUpdate({ currentStep: getPreviousStep() })}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          ← Back
        </button>
        <button
          onClick={() => onUpdate({ currentStep: getNextStep() })}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Continue to Preview →
        </button>
      </div>
    </div>
  )
}